import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import type * as React from "react";
import { cn } from "#lib/utils";

// Mounted once at the root: the first tooltip waits the library's 600 ms, and one opened within
// `timeout` of the last shows at once, so moving along a row of icons does not wait at each.
function TooltipProvider({ ...props }: TooltipPrimitive.Provider.Props) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" {...props} />;
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

// `--popover`, not an inverted fill: a tooltip carries a shortcut people read, and
// `contrast.test.ts` holds it to the same floor as every other surface.
function TooltipContent({
  className,
  side = "top",
  sideOffset = 6,
  align = "center",
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className="isolate z-[var(--z-tooltip)]"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            "z-50 inline-flex w-fit max-w-[var(--tooltip-max-w)] popup-motion items-center gap-1.5 rounded-row border border-edge bg-popover px-[var(--cell-px)] py-1 text-label text-foreground shadow-lift-2",
            className,
          )}
          {...props}
        >
          {children}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

/**
 * A control drawn as an icon alone, named on hover, with its shortcut's keys after the name. Other
 * props go to `Tooltip.Trigger`, which hands the element what it does not use itself (`disabled` it
 * keeps), so a Base UI trigger can render it.
 */
function Tip({
  label,
  keys,
  side,
  children,
  ...props
}: Omit<TooltipPrimitive.Trigger.Props, "render" | "children"> & {
  readonly label: string;
  readonly keys?: React.ReactNode;
  readonly side?: TooltipPrimitive.Positioner.Props["side"];
  readonly children: React.ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger render={children} {...props} />
      {/* A touch screen has no hover, and a tap would leave the name standing over the screen. */}
      <TooltipContent side={side} className="pointer-coarse:hidden">
        {label}
        {keys}
      </TooltipContent>
    </Tooltip>
  );
}

export { Tip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
